import { useState, useEffect, useRef } from "react";
import { lookupStreets, addressSupported } from "@/lib/address";

/**
 * StreetAutocomplete – Straßennamen-Vorschläge (OpenPLZ) für ein Straßen-Feld.
 * Nicht-strikt: Freitext bleibt immer möglich, Vorschläge sind optional.
 */
export function StreetAutocomplete({
  value,
  onChange,
  country = "DE",
  postalCode = "",
  locality = "",
  className = "",
  wrapperClassName = "",
  placeholder,
  testId,
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const skipRef = useRef(false);

  useEffect(() => {
    if (!addressSupported(country)) { setSuggestions([]); setOpen(false); return; }
    if (skipRef.current) { skipRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    const h = setTimeout(async () => {
      const res = await lookupStreets(country, postalCode, q, locality);
      const filtered = res.filter((s) => s.toLowerCase() !== q.toLowerCase());
      setSuggestions(filtered.slice(0, 8));
      setOpen(filtered.length > 0);
    }, 300);
    return () => clearTimeout(h);
  }, [value, country, postalCode, locality]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => { skipRef.current = true; onChange(s); setOpen(false); setSuggestions([]); };

  return (
    <div className={`relative ${wrapperClassName}`} ref={boxRef}>
      <input
        data-testid={testId}
        value={value}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        className={className}
        placeholder={placeholder}
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid={testId ? `${testId}-suggestions` : undefined}
          className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-lg border border-[#E5E5E5] bg-white shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li key={`${s}-${i}`}>
              <button
                type="button"
                data-testid={testId ? `${testId}-option-${i}` : undefined}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F5] text-[#0A0A0A]"
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default StreetAutocomplete;
